import { CalendarIcon } from '@heroicons/react/24/outline';
import { Maybe } from 'maybeasy';
import { observer } from 'mobx-react';
import { T } from '../../../../../../Translations';
import { usePreviewLicense } from '../../../Preview/AdminPreview/PreviewLicenseContext';
import { FUTURE_LICENSE_TINT_CLASSES, LicenseStarts } from './FutureLicense';

interface UnlicensedBadgeProps {
  productLicenseTypeIds: number[];
  // DVLP-7601: when Just, the badge falls back to the admin-preview
  // entitlement set for the currently-selected simulated PLT (instead of
  // the org's real licenses). Nothing = use the org-based check.
  experienceId: Maybe<number>;
  // Just when the org's only licenses for this course haven't started yet.
  licenseStartsOn: Maybe<Date>;
}

function UnlicensedBadge({
  productLicenseTypeIds,
  experienceId,
  licenseStartsOn,
}: UnlicensedBadgeProps) {
  // PreviewLicenseContext returns a stable default (empty options, no
  // selection) outside AdminPreview, so this stays a no-op in edit mode.
  const { options, selectedPltId, entitledExperienceIds } = usePreviewLicense();
  const previewActive = options.length > 0 && selectedPltId.isJust();

  // product_license_type_ids contains only license types the org has active
  // licenses for. Empty = org has no matching licenses (org-based check).
  const orgBasedCheck = productLicenseTypeIds.length === 0;
  const isUnlicensed = previewActive
    ? experienceId.map((id) => !entitledExperienceIds.has(id)).getOrElseValue(orgBasedCheck)
    : orgBasedCheck;

  if (!isUnlicensed) {
    // The start date is about the org's own licenses, not a simulated one.
    if (previewActive) {
      return null;
    }

    return licenseStartsOn
      .map((startsOn) => (
        <div
          className="absolute top-2 left-2 z-10"
          data-intercom-target="future-license-badge-wrapper"
        >
          <span
            className={`inline-flex items-center gap-1 px-2 py-1 border rounded text-xs font-medium shadow-sm ${FUTURE_LICENSE_TINT_CLASSES}`}
            data-intercom-target="future-license-badge"
          >
            <CalendarIcon className="w-3.5 h-3.5" aria-hidden="true" />
            <LicenseStarts startsOn={startsOn} />
          </span>
        </div>
      ))
      .getOrElseValue(<></>);
  }

  return (
    <div className="absolute top-2 left-2 z-10" data-intercom-target="unlicensed-badge-wrapper">
      <span
        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-muted text-muted-foreground text-xs font-medium shadow-sm"
        data-intercom-target="unlicensed-badge"
      >
        <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>
        <T kind="Unlicensed" />
      </span>
    </div>
  );
}

export default observer(UnlicensedBadge);
